import { useContext } from "react";
import { AuthContext } from "common/AuthProvider";

// Permission names for the Shifts area.
//
// These are the exact strings stored in dbo.Permission. AuthProvider compares them with a
// case-sensitive ===, so the casing here has to match the seed migration
// (20260813_AALLAN_01_AddShiftAreaPermissions.sql) character for character.
//
// The split follows the role matrix Heidi and Andrew signed off on:
//
//   Yard Daily        Admin full · Field Manager full        · Viewer read only
//   Yard Schedule     Admin full · Field Manager allocations · Viewer read only  (times Admin only)
//   Yard Review       Admin full · everyone else read only
//   Yard Shift Update Admin full · everyone else read only    (visible to all -- Heidi, 08/13)
//   Shift Types       Admin only, hidden from everyone else
//   Cross-Reference   Admin only, hidden from everyone else
//   Approved checkbox Admin only
//
// AccessShifts stays the area gate: every role that reaches /shifts holds it, and viewing any
// of the four shared tabs needs nothing further. Only the two hidden tabs get their own access
// permission, because those are the only ones where visibility itself is restricted.
export const ShiftPermission = {
  /** Area gate -- the Shifts menu and the /shifts route. Seeded 2026-08-07. */
  Access: "AccessShifts",
  /** Edit a Yard Daily instance, and import a week from the master schedule. */
  EditYardDaily: "EditYardDaily",
  /** Yard Schedule allocations: Driver, Vehicle, Client, Type -- plus adding a shift. */
  EditYardSchedule: "EditYardSchedule",
  /**
   * Yard Schedule start/end times. Deliberately separate from EditYardSchedule: the App feeds
   * these and payroll reads them, so a field manager allocating drivers must not move them.
   */
  EditYardScheduleTimes: "EditYardScheduleTimes",
  /** Edit the Yard Shift Update grid. */
  EditShiftUpdate: "EditShiftUpdate",
  /**
   * Approving a shift -- the Approved checkbox on Yard Shift Update and the Approve button on
   * Yard Review. One permission, because both approve the same YardDaily row.
   *
   * Kept separate from EditShiftUpdate so approval can be withheld from someone who is
   * otherwise allowed to correct shift data. That is exactly the question Tim raised, and
   * today the answer is Admin only.
   */
  ApproveShift: "ApproveShift",
  /** See the Shift Types tab at all. */
  AccessShiftTypes: "AccessShiftTypes",
  /** See the Cross-Reference tab at all. */
  AccessCrossReference: "AccessShiftCrossReference",
} as const;

export interface ShiftPermissions {
  canEditYardDaily: boolean;
  canEditYardSchedule: boolean;
  canEditYardScheduleTimes: boolean;
  canEditShiftUpdate: boolean;
  canApproveShift: boolean;
}

/**
 * What the signed-in user may change in the Shifts area.
 *
 * Read-only is the default: a screen asks for the permission it needs and disables its inputs
 * when the answer is no, rather than assuming edit rights and hiding them from some roles.
 */
export const useShiftPermissions = (): ShiftPermissions => {
  const { userHasPermission } = useContext(AuthContext);

  return {
    canEditYardDaily: userHasPermission(ShiftPermission.EditYardDaily),
    canEditYardSchedule: userHasPermission(ShiftPermission.EditYardSchedule),
    canEditYardScheduleTimes: userHasPermission(ShiftPermission.EditYardScheduleTimes),
    canEditShiftUpdate: userHasPermission(ShiftPermission.EditShiftUpdate),
    canApproveShift: userHasPermission(ShiftPermission.ApproveShift),
  };
};

/** Standard banner copy, so every read-only screen in the area says the same thing. */
export const READ_ONLY_MESSAGE = "You have view-only access to this screen. Contact an administrator to make changes.";
